import { z } from "zod";
import {
	AdminListInputSchema,
	adminItemOutput,
	adminListOutput,
} from "./common";

// =============
// Admin Partner Schemas
// =============

/**
 * Admin partner schema - includes deletedAt for soft delete visibility.
 */
export const DeactivationReasonSchema = z.enum([
	"cleanup",
	"fraud",
	"terms_violation",
	"other",
]);
export type DeactivationReason = z.infer<typeof DeactivationReasonSchema>;

export const AdminPartnerSchema = z.object({
	id: z.string(),
	createdByUserId: z.string().nullable(), // Nullable if creator was deleted
	createdByUserEmail: z.string().nullish(), // Joined from user table
	name: z.string(),
	email: z.email(),
	stripeAccountId: z.string().nullable(),
	stripeOnboardingStatus: z
		.enum(["creating", "pending", "complete", "restricted"])
		.nullable(),
	stripePayoutsEnabled: z.boolean().nullable(),
	createdAt: z.coerce.date(),
	deletedAt: z.coerce.date().nullable(),
	deactivationReason: DeactivationReasonSchema.nullable(),
});
export type AdminPartner = z.infer<typeof AdminPartnerSchema>;

/**
 * Admin partner list input with partner-specific filters.
 */
export const AdminPartnerListInputSchema = AdminListInputSchema.extend({
	filter: z
		.object({
			name: z.string().optional(),
			email: z.string().optional(),
			createdByUserId: z.string().optional(),
			stripeOnboardingStatus: z
				.enum(["creating", "pending", "complete", "restricted"])
				.optional(),
			stripePayoutsEnabled: z.coerce.boolean().optional(),
		})
		.optional(),
});
export type AdminPartnerListInput = z.infer<typeof AdminPartnerListInputSchema>;

/**
 * Admin partner list output.
 */
export const AdminPartnerListOutputSchema = adminListOutput(AdminPartnerSchema);
export type AdminPartnerListOutput = z.infer<
	typeof AdminPartnerListOutputSchema
>;

/**
 * Admin partner single item output.
 */
export const AdminPartnerOutputSchema = adminItemOutput(AdminPartnerSchema);
export type AdminPartnerOutput = z.infer<typeof AdminPartnerOutputSchema>;

/**
 * Admin partner create input.
 */
export const AdminPartnerCreateInputSchema = z.object({
	createdByUserId: z.string(),
	name: z.string().min(1),
	email: z.string().email(),
});
export type AdminPartnerCreateInput = z.infer<
	typeof AdminPartnerCreateInputSchema
>;

/**
 * Admin partner update input.
 */
export const AdminPartnerUpdateInputSchema = z.object({
	id: z.string(),
	name: z.string().min(1).optional(),
	email: z.string().email().optional(),
});
export type AdminPartnerUpdateInput = z.infer<
	typeof AdminPartnerUpdateInputSchema
>;

/**
 * Admin partner delete input.
 * Includes optional deactivation reason to distinguish cleanup from fraud/abuse.
 */
export const AdminPartnerDeleteInputSchema = z.object({
	id: z.string(),
	deactivationReason: DeactivationReasonSchema.default("cleanup"),
});
export type AdminPartnerDeleteInput = z.infer<
	typeof AdminPartnerDeleteInputSchema
>;
